import { FastForward, AlertTriangle, X, Coffee, Dumbbell, ShieldCheck, ScrollText, User, ArrowRight } from "lucide-react";

export const SkipAhead = ({ day, horse, summary, busy, onConfirm, onCancel, onContinue, onViewJournal, onViewProfile }) => {
  const end = Math.min(120, day + 9);

  // ---------- Summary phase ----------
  if (summary) {
    const s = summary;
    const before = s.readiness_before, after = s.readiness_after;
    return (
      <div className="fixed inset-0 z-[85] flex items-end justify-center bg-black/50 p-3 sm:items-center" data-testid="skip-summary">
        <div className="flex max-h-[88vh] w-full max-w-md flex-col rounded-2xl border border-border bg-card shadow-2xl">
          <div className="flex items-center justify-between border-b border-border p-4">
            <h3 className="font-display text-xl font-bold text-primary">Days {s.start_day}–{s.end_day} Complete</h3>
            <button data-testid="skip-summary-close" onClick={onContinue} className="rounded-full p-1 text-muted-foreground hover:bg-muted"><X size={18} /></button>
          </div>
          <div className="overflow-y-auto p-4">
            {before && after && (
              <div className="mb-3 flex items-center justify-center gap-2 rounded-xl bg-muted/60 p-2.5 text-xs font-bold" data-testid="skip-readiness-change">
                <ShieldCheck size={14} className="text-primary" />
                Readiness: {before.level} ({before.score}%) <ArrowRight size={12} /> {after.level} ({after.score}%)
              </div>
            )}
            <div className="space-y-1.5">
              {s.days.map((d) => (
                <div key={d.day} className="rounded-lg border border-border bg-card px-3 py-2 text-xs" data-testid={`skip-day-${d.day}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-primary">Day {d.day}: {d.kind === "rest" ? "Rest & Care Day" : d.activities.join(" and ")}</span>
                    {d.kind === "rest" ? <Coffee size={13} className="shrink-0 text-secondary" /> : <Dumbbell size={13} className="shrink-0 text-primary" />}
                  </div>
                  {(Object.keys(d.gains).length > 0 || d.skills_mastered.length > 0 || d.trust_delta !== 0) && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {d.trust_delta !== 0 && <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${d.trust_delta >= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>trust {d.trust_delta >= 0 ? "+" : ""}{d.trust_delta}</span>}
                      {Object.entries(d.gains).map(([k, v]) => (
                        <span key={k} className={`rounded px-1.5 py-0.5 text-[9px] font-bold capitalize ${v >= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{k} {v >= 0 ? "+" : ""}{v}</span>
                      ))}
                      {d.skills_mastered.map((m) => <span key={m} className="rounded bg-secondary/15 px-1.5 py-0.5 text-[9px] font-bold text-secondary">Mastered: {m}</span>)}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {s.missing_basics.length > 0 && (
              <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-2.5 text-[11px] font-semibold text-amber-800" data-testid="skip-missing-basics">
                Still missing core basics: {s.missing_basics.join(", ")}. Train these manually before competition!
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 border-t border-border p-3">
            <button data-testid="skip-continue-btn" onClick={onContinue} className="col-span-2 h-11 rounded-xl bg-primary font-bold text-primary-foreground active:scale-95">Continue</button>
            <button data-testid="skip-view-journal-btn" onClick={onViewJournal} className="flex items-center justify-center gap-1 rounded-xl border border-border py-2.5 text-xs font-bold text-primary active:scale-95"><ScrollText size={13} /> Full Journal</button>
            <button data-testid="skip-view-profile-btn" onClick={onViewProfile} className="flex items-center justify-center gap-1 rounded-xl border border-border py-2.5 text-xs font-bold text-primary active:scale-95"><User size={13} /> Horse Profile</button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Confirmation / preview phase ----------
  return (
    <div className="fixed inset-0 z-[85] flex items-end justify-center bg-black/50 p-3 sm:items-center" data-testid="skip-confirm">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-xl font-bold text-primary"><FastForward size={20} /> Skip Ahead 10 Days</h3>
          <button data-testid="skip-cancel-x" onClick={onCancel} className="rounded-full p-1 text-muted-foreground hover:bg-muted"><X size={18} /></button>
        </div>
        <div className="mt-3 rounded-xl bg-muted/60 p-3 text-sm">
          <p className="font-bold text-primary">Days {day}–{end}</p>
          <p className="mt-1 text-muted-foreground">Currently Day {day} → will advance to Day {Math.min(120, day + 10)}.</p>
          <ul className="mt-2 space-y-1 text-xs text-foreground/80">
            <li>• 8 randomly-assigned training days</li>
            <li>• 2 Rest & Care Days</li>
            <li>• Free grass hay & fresh water every day</li>
          </ul>
        </div>
        <div className="mt-3 space-y-1 text-xs text-muted-foreground">
          <p>• Activities are <span className="font-bold">randomly assigned</span> from what's currently available.</p>
          <p>• Required basics may <span className="font-bold">not</span> be prioritized.</p>
          <p>• Manual training gives you much more control.</p>
        </div>
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs font-semibold text-amber-800" data-testid="skip-warning">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          Skipped training is randomly assigned. It may not improve the skills your horse needs most. Manual training gives you much more control over your horse's development and competition preparation.
        </div>
        <div className="mt-4 flex gap-2">
          <button data-testid="skip-cancel-btn" onClick={onCancel} className="h-12 flex-1 rounded-xl border-2 border-border bg-card font-bold text-primary active:scale-95">Cancel</button>
          <button data-testid="skip-confirm-btn" disabled={busy} onClick={onConfirm} className="h-12 flex-1 rounded-xl bg-secondary font-bold text-white disabled:opacity-60 active:scale-95">{busy ? "Skipping…" : "Confirm Skip"}</button>
        </div>
      </div>
    </div>
  );
};
